import {
  ChevronDown,
  ChevronUp,
  ImageOff,
  Star,
  X,
} from "lucide-react"
import { useRef, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import type { StudioGuideItem, TimeContext } from "@/lib/types"

// ---------------------------------------------------------------------------
// Time context helpers
// ---------------------------------------------------------------------------

const TIME_LABELS: Record<string, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  all_day: "All day",
}

const TIME_OPTIONS: { value: TimeContext; label: string }[] = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
  { value: "all_day", label: "All day" },
]

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GuideItemRowProps {
  item: StudioGuideItem
  index: number
  isFirst: boolean
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
  onChange: (patch: Partial<StudioGuideItem>) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GuideItemRow({
  item,
  index,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onRemove,
  onChange,
}: GuideItemRowProps) {
  const [expanded, setExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function toggle() {
    setExpanded((prev) => {
      if (!prev) {
        requestAnimationFrame(() => textareaRef.current?.focus())
      }
      return !prev
    })
  }

  const poi = item.poi
  const categories = poi?.categories ?? []
  const hasCover = !!poi?.cover_url

  return (
    <li className="group">
      {/* Main row */}
      <div
        className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-muted/50"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            toggle()
          }
        }}
      >
        {/* Position number */}
        <span className="w-6 text-right font-mono text-sm text-muted-foreground">
          {index + 1}
        </span>

        {/* Cover thumbnail placeholder */}
        <div className="flex size-8 shrink-0 items-center justify-center rounded bg-muted text-xs text-muted-foreground">
          {poi?.cover_url ? (
            <img
              src={poi.cover_url}
              alt=""
              className="size-8 rounded object-cover"
            />
          ) : (
            <span className="text-[10px]">
              {categories[0]?.name.charAt(0).toUpperCase() ?? "?"}
            </span>
          )}
        </div>

        {/* Highlight toggle */}
        <button
          type="button"
          className="shrink-0"
          onClick={(e) => {
            e.stopPropagation()
            onChange({ is_highlighted: !item.is_highlighted })
          }}
          aria-label={item.is_highlighted ? "Remove highlight" : "Add highlight"}
        >
          <Star
            className={`size-4 ${
              item.is_highlighted
                ? "fill-amber-400 text-amber-400"
                : "text-muted-foreground/40"
            }`}
          />
        </button>

        {/* POI name */}
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {poi?.name ?? "Unknown"}
        </span>

        {/* Time context badge */}
        {item.time_context && (
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {TIME_LABELS[item.time_context] ?? item.time_context}
          </Badge>
        )}

        {/* Category badges (first 2) */}
        {categories.slice(0, 2).map((c) => (
          <span
            key={c.id}
            className="hidden shrink-0 text-xs text-muted-foreground sm:inline"
          >
            {c.name}
          </span>
        ))}

        {/* Warning: no cover */}
        {!hasCover && poi && (
          <ImageOff className="size-3.5 shrink-0 text-amber-500" />
        )}

        {/* Hover-revealed actions */}
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={isFirst}
            onClick={(e) => {
              e.stopPropagation()
              onMoveUp()
            }}
            aria-label="Move up"
          >
            <ChevronUp className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={isLast}
            onClick={(e) => {
              e.stopPropagation()
              onMoveDown()
            }}
            aria-label="Move down"
          >
            <ChevronDown className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            aria-label="Remove from guide"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {/* Expanded inline editor */}
      {expanded && (
        <div className="space-y-3 border-t bg-muted/30 px-4 py-3">
          {/* Curator note */}
          <div className="grid gap-1">
            <label
              htmlFor={`note-${item.id}`}
              className="text-xs text-muted-foreground"
            >
              Curator note
            </label>
            <Textarea
              ref={textareaRef}
              id={`note-${item.id}`}
              value={item.host_note ?? ""}
              onChange={(e) =>
                onChange({ host_note: e.target.value || null })
              }
              rows={2}
              placeholder="Optional note visible to guests..."
              className="text-xs"
            />
          </div>

          {/* Time context buttons */}
          <div className="grid gap-1">
            <span className="text-xs text-muted-foreground">Time context</span>
            <div className="flex flex-wrap gap-1.5">
              {TIME_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={item.time_context === opt.value ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    onChange({
                      time_context:
                        item.time_context === opt.value ? null : opt.value,
                    })
                  }
                >
                  {opt.label}
                </Button>
              ))}
              {item.time_context && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() => onChange({ time_context: null })}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  )
}
